<script lang="ts">
  import OVIcon from "$components/+shared/OVIcon/OVIcon.svelte";
  import StackedAvatars from "$components/+shared/StackedAvatars/StackedAvatars.svelte";
  import Dropdown from "$components/+design-system/Dropdown/Dropdown.svelte";
  import Upvote from "../Upvotes/Upvote.svelte";
  import { communitySlug, logoImage } from "app/stores/community";
  import CreationTimeInterval from "$components/+shared/CreationTimeInterval.svelte";
  import { labels, labelsFetched } from "$stores/labels";
  import { tooltip } from "$actions/tooltip";
  import Reader from "app/components/editor/Reader.svelte";
  import { fade } from "svelte/transition";
  import LoadingAtom from "$components/+shared/Loading/LoadingAtom.svelte";
  import AuthorDetails from "../cards/ForumFields/AuthorDetails.svelte";
  import Reactions from "$components/forum/Reactions/Reactions.svelte";
  import {
    CheckFilled,
    DashedCircle,
    Dot,
    EyeSlash,
    LockSolid,
    PinFilled,
    VerticalDots,
  } from "$components/+shared/OVIcon";
  import ToolTip from "app/components/+shared/ToolTip/ToolTip.svelte";
  import IconButtonTrigger from "$components/+design-system/Dropdown/fields/IconButtonTrigger.svelte";
  import Action from "$components/+design-system/Dropdown/fields/Action.svelte";
  import Avatar from "app/components/+shared/Avatar/Avatar.svelte";
  import { memberProfilePanel } from "app/stores/memberPanel";
  import Image from "app/components/+shared/Image/Image.svelte";
  import Separator from "app/components/+shared/Separator/Separator.svelte";
  import Video from "app/components/+shared/Video/Video.svelte";
  import { getEmbedURLFromYoutubeURL } from "app/components/editor/extensions/Embeds/utils";
  import cx from "classnames";
  import api from "api";
  import type { JSONContent } from "@tiptap/core";
  import type { AvatarDetails } from "models/shared-types";
  import type { Thread as ThreadType } from "models/forums";
  import type { DropdownAction } from "$components/+design-system/Dropdown/fields/Action.svelte";

  export let thread: ThreadType;
  export let menuOptions: DropdownAction[] = [];
  export let canUpvote: boolean;
  export let isThreadUpvotesEnabled: boolean;
  export let avatars: AvatarDetails[];
  export let isQuote: boolean = false;

  const MAX_LABELS_VISIBLE = 1;
  let fadeThread = false;
  let contentRef: HTMLElement;

  $: {
    // fade thread if content is too long
    const height = contentRef?.clientHeight;
    fadeThread = height < contentRef?.scrollHeight;
  }

  $: isTeam = thread.author?.roles?.find((role) => role.isTeam);

  // media
  let image: string | null = null;
  let video: string | null = null;
  let youtube: string | null = null;

  // show first media object if a normal card
  $: if (thread.pinned && thread.maximized) {
    image = video = youtube = null;
  } else {
    findFirstMedia(thread.content);
  }

  function findFirstMedia(content: JSONContent) {
    const media = content.content?.find(
      (item) =>
        item.type === "image" ||
        item.type === "open-graph" ||
        item.type === "video" ||
        (item.type === "iframe-embed" && item?.attrs?.type === "youtube")
    );

    if (!media) return;

    if (media.type === "image") {
      image = media.attrs?.src;
    } else if (media.type === "open-graph") {
      image = media.attrs?.openGraph?.image?.[0];
    } else if (media.type === "video") {
      video = media.attrs?.src;
    } else {
      youtube = media.attrs?.src;
    }
  }
</script>

<div
  class="flex flex-col overflow-hidden rounded-lg border-[0.5px] border-black/20 bg-white p-4 transition-colors hover:bg-black/3 dark:border-white/10 dark:bg-white/3 dark:hover:bg-white/5"
  class:h-[364px]={!(thread.maximized && thread.pinned)}
  class:h-auto={thread.maximized && thread.pinned}
>
  <div class="mb-4 flex items-center gap-2">
    <!-- show normal thread author info -->
    {#if thread.maximized && thread.pinned}
      <Avatar
        mainImage={thread.author.profileImage}
        backgroundColor={thread.author.profileColor}
        label={thread.author.handle && thread.author.handle[0]}
        size="size-6"
        radius="rounded"
      />
      <AuthorDetails author={thread.author} type={"thread"} showAuthorBadge={false} />
    {:else}
      <!-- show slimmed down author info -->
      <button
        class="not-prose group inline-flex items-center gap-2 hover:cursor-pointer"
        on:click|stopPropagation|preventDefault={() =>
          thread.author && memberProfilePanel.clearAndShow(thread.author.uuid, $communitySlug)}
        on:keydown={(event) =>
          event.key === "Enter" &&
          thread.author &&
          memberProfilePanel.clearAndShow(thread.author.uuid, $communitySlug)}
        aria-label="open profile for {thread.author.handle}"
      >
        <span
          class="text-xs font-normal text-neutral-500 transition-colors group-hover:text-gray-800 dark:text-neutral-400 dark:group-hover:text-gray-300"
        >
          {thread.author.handle}
        </span>
        {#if isTeam}
          <ToolTip>
            <span slot="tip">Team member</span>
            <Avatar
              mainImage={$logoImage}
              backgroundColor={thread.author.profileColor}
              wrapperClasses="bg-transparent"
              label={thread.author.handle && thread.author.handle[0]}
              radius="rounded-[3px]"
            />
          </ToolTip>
        {/if}
      </button>
    {/if}

    <div class="ml-auto mr-0.5 flex items-center gap-2.5">
      {#if thread.locked}
        <ToolTip>
          <span slot="tip">Thread locked</span>
          <OVIcon icon={LockSolid} class="text-gray-400" />
        </ToolTip>
      {/if}
      {#if thread.pinned}
        <ToolTip>
          <span slot="tip">Pinned</span>
          <OVIcon icon={PinFilled} class="text-gray-400" />
        </ToolTip>
      {/if}
      {#if thread.hidden}
        <span class="text-[#F5656C]">
          <OVIcon icon={EyeSlash} />
        </span>
      {/if}
    </div>
    <slot name="solutionStatus" />

    <CreationTimeInterval
      time={thread.lastRepliedAt}
      full={false}
      fontClasses="text-xs font-normal text-neutral-500 dark:text-neutral-400"
    />

    {#if !!menuOptions?.length}
      <Dropdown>
        <IconButtonTrigger
          customIconSize="h-[18px]"
          size="small"
          slot="trigger"
          let:active
          {active}
          icon={VerticalDots}
          a11yLabel="More actions"
        />
        {#each menuOptions as field}
          <Action title={field.title} icon={field.icon} on:click={field.onClick} />
        {/each}
      </Dropdown>
    {/if}
  </div>

  <div
    class={cx("relative overflow-hidden leading-none", {
      "[mask-image:_linear-gradient(to_top,_transparent_2%,_black_48%)]":
        !(thread.maximized && thread.pinned) && fadeThread,
    })}
    bind:this={contentRef}
  >
    <div class="flex items-start justify-between gap-2.5">
      <div class="w-full">
        <div class="mb-4 flex items-center gap-2">
          {#if thread.solvable}
            <div class="shrink-0">
              {#if thread.solved}
                <OVIcon icon={CheckFilled} class="text-green-400" />
              {:else}
                <OVIcon icon={DashedCircle} class="text-gray-400" />
              {/if}
            </div>
          {/if}
          <h3
            class="line-clamp-1 text-sm font-semibold text-gray-600 dark:text-white"
            use:tooltip
            title={thread.title}
          >
            {thread.title}
          </h3>
        </div>

        {#if image}
          <Image
            class="h-[220px] w-full rounded-lg bg-gray-200 dark:bg-gray-200/20"
            cropPosition={null}
            quality="normal"
            src={image}
            fallback={!image?.startsWith("https://ucarecdn.com/")}
            alt={`Image for thread titled: ${thread.title}`}
          />
        {:else if video}
          <Video class="h-[220px] w-full rounded-lg object-cover" src={video} isThumbnail />
        {:else if youtube}
          <iframe
            title="Embedded content"
            class="h-[220px] w-full rounded-lg"
            src={getEmbedURLFromYoutubeURL({
              url: youtube,
            })}
            allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          />
        {:else}
          <div class="text-gray-400">
            <Reader content={thread.content} disableLinksInSSR />
          </div>
        {/if}
      </div>
    </div>
  </div>

  <div class="mt-auto flex shrink-0 items-center gap-2 overflow-clip overflow-x-hidden pt-4">
    {#if isThreadUpvotesEnabled}
      <Upvote
        variant="horizontal-small"
        disabled={!canUpvote}
        count={thread.upvoteCount}
        isSelected={thread.userUpvoted}
        addUpvote={() =>
          api.communitiesThreadsUpvote({
            communitySlug: $communitySlug,
            uuid: thread.uuid,
          })}
        removeUpvote={() =>
          api.communitiesThreadsRemoveUpvote({
            communitySlug: $communitySlug,
            uuid: thread.uuid,
          })}
      />
      <Separator height="h-5" class="w-[0.5px]" />
    {/if}
    <span
      class="text flex shrink-0 items-center space-x-1 rounded-lg dark:text-gray-400 {isQuote
        ? 'text-gray-400'
        : 'text-gray-500'}"
    >
      {#if thread.repliers?.length}
        <span in:fade>
          <StackedAvatars {avatars} size="size-5" textSize="text-xs" radius="rounded-[3px]" />
        </span>
      {:else}
        {#each Array(Math.min(thread.replyCount, 3)) as _}
          <LoadingAtom size="size-5" radius="rounded-[3px]" />
        {/each}
      {/if}
      <span class="shrink-0 px-1 text-xs font-medium text-blue-300">
        {thread.replyCount}
        {thread.replyCount === 1 ? "reply" : "replies"}
      </span>
    </span>

    {#if $labelsFetched && thread.labels?.length}
      <div class="ml-auto flex shrink items-center gap-1.5" in:fade>
        {#each thread.labels.slice(0, MAX_LABELS_VISIBLE) as uuid (uuid)}
          {@const label = $labels[uuid]}
          <div
            class="flex w-fit items-center gap-1 overflow-hidden rounded bg-black/5 px-1 py-0.5 dark:bg-white/5"
          >
            <span style:color={label.color}>
              <OVIcon icon={Dot} size="size-3" />
            </span>
            <span
              class="max-w-[14ch] truncate text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              {label?.name}
            </span>
            {#if label.teamOnly}
              <span
                use:tooltip
                title="Only team members can see this label"
                class="text-gray-500 dark:text-gray-400"
              >
                <OVIcon icon={LockSolid} size="size-3.5" />
              </span>
            {/if}
          </div>
        {/each}
        {#if thread.labels.length > MAX_LABELS_VISIBLE}
          <span
            use:tooltip
            title={thread.labels
              .slice(MAX_LABELS_VISIBLE)
              .map((uuid) => $labels[uuid].name)
              .join(", ")}
            class="rounded bg-black/5 px-1 py-0.5 text-xxs font-medium text-gray-500 dark:bg-white/5 dark:text-gray-400"
          >
            +{thread.labels.length - MAX_LABELS_VISIBLE}
          </span>
        {/if}
      </div>
    {/if}
    <!-- Show reactions when pinned to top and full-width -->
    {#if thread.pinned && thread.maximized && !!thread.reactions?.length}
      <Reactions reactions={thread.reactions} />
    {/if}
  </div>
</div>
